import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import { useConvexAuth, useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';

function normalizeName(raw: string | null | undefined) {
  return (raw ?? '').trim();
}

export function AccountPage() {
  const convexAuth = useConvexAuth();
  const { signIn, signOut } = useAuth();
  const user = useQuery(api.users.getCurrentUser, {});
  const orgs = useQuery(api.organizations.listForUser, {});
  const createOrganization = useMutation(api.organizations.create);
  const [newOrgName, setNewOrgName] = useState('My Team');
  const [isSavingOrg, setIsSavingOrg] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createMessage, setCreateMessage] = useState<string | null>(null);

  const isSignedIn = convexAuth.isAuthenticated;
  const isLoading = user === undefined || convexAuth.isLoading || orgs === undefined;

  const normalizedUser = normalizeName(user?.name);
  const normalizedEmail = normalizeName(user?.email);

  const handleCreateOrganization = async () => {
    const name = normalizeName(newOrgName);
    if (!name) {
      setCreateError('Enter an organization name first.');
      return;
    }
    setIsSavingOrg(true);
    setCreateError(null);
    setCreateMessage(null);
    try {
      const orgId = await createOrganization({ name });
      setCreateMessage(`Created organization ${orgId ? 'successfully' : 'successfully'}.`);
      setNewOrgName('My Team');
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Could not create organization.');
    } finally {
      setIsSavingOrg(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090b0f] text-[#edf2f7] px-4 py-10">
      <div className="mx-auto max-w-4xl space-y-5">
        <header className="rounded-md border border-[#223042] bg-[#0d1117] p-5">
          <p className="text-xs uppercase tracking-[0.12em] text-[#6cc9ff]">Shared Coordinator</p>
          <h1 className="mt-1 text-2xl font-semibold">Account</h1>
          <p className="mt-1 max-w-2xl text-sm text-[#9fb0c3]">
            Sign in, manage account context, and jump to pairing workflows for browser access and cross-device library browsing.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/pair"
              className="rounded bg-[#1b2938] px-3 py-2 text-sm text-[#dbe6f2] transition hover:bg-[#273a4f]"
            >
              Pairing helper
            </Link>
            <Link
              to="/library"
              className="rounded bg-[#1b2938] px-3 py-2 text-sm text-[#dbe6f2] transition hover:bg-[#273a4f]"
            >
              Browser library
            </Link>
            <Link
              to="/setup"
              className="rounded bg-[#1b2938] px-3 py-2 text-sm text-[#dbe6f2] transition hover:bg-[#273a4f]"
            >
              Setup guide
            </Link>
          </div>
        </header>

        <section className="rounded-md border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium">Session</h2>
          {isLoading ? (
            <p className="mt-2 text-sm text-[#7f8fa3]">Checking account state…</p>
          ) : isSignedIn ? (
            <div className="mt-2 space-y-3">
              <p className="text-sm">
                Signed in as <span className="font-mono text-[#62d3b5]">{normalizedUser || normalizedEmail || 'User'}</span>
              </p>
              <p className="text-xs text-[#7f8fa3]">{normalizedEmail || 'No email on file'}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void signOut()}
                  className="rounded bg-[#3b1414] px-4 py-2 text-sm text-[#ffd0d0] hover:bg-[#522020]"
                >
                  Sign out
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-2 space-y-2">
              <p className="text-sm text-[#9fb0c3]">You are not signed in on this browser.</p>
              <button
                type="button"
                onClick={() => void signIn()}
                className="rounded bg-[#62d3b5] px-4 py-2 text-sm font-medium text-[#0c1117] hover:bg-[#50bea0]"
              >
                Sign in with Google
              </button>
            </div>
          )}
        </section>

        <section className="rounded-md border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium">Organizations (pairs by account)</h2>
          <p className="mt-1 text-xs text-[#9fb0c3]">Organizations and memberships here map directly to pairing/account scope.</p>
          <div className="mt-4">
            {orgs && orgs.length > 0 ? (
              <ul className="space-y-2 text-sm">
                {orgs.map((org) => (
                  <li
                    key={org._id}
                    className="rounded border border-[#263342] bg-[#090b0f] px-3 py-2 text-[#d9e2ec]"
                  >
                    {org.name}
                    <span className="ml-2 text-[11px] text-[#7f8fa3]">({org.memberCount} member{org.memberCount === 1 ? '' : 's'})</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[#7f8fa3]">
                No organizations yet. You can create one below for shared pairing across devices.
              </p>
            )}
          </div>

          <div className="mt-5">
            <label className="text-xs text-[#9fb0c3] block" htmlFor="newOrgName">
              Create a new organization
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              <input
                id="newOrgName"
                value={newOrgName}
                onChange={(event) => setNewOrgName(event.target.value)}
                placeholder="my-event-team"
                className="w-72 rounded border border-[#2b3a4d] bg-[#090b0f] px-3 py-2 text-sm text-[#edf2f7] outline-none focus:border-[#62d3b5]"
              />
              <button
                type="button"
                onClick={() => void handleCreateOrganization()}
                disabled={isSavingOrg || !isSignedIn}
                className="rounded bg-[#24384d] px-4 py-2 text-sm text-[#e4ecf4] disabled:opacity-50"
              >
                {isSavingOrg ? 'Creating…' : 'Create'}
              </button>
            </div>
            {createError ? <p className="mt-2 text-xs text-[#ff6b6b]">{createError}</p> : null}
            {createMessage ? <p className="mt-2 text-xs text-[#62d3b5]">{createMessage}</p> : null}
          </div>
        </section>
      </div>
    </div>
  );
}

export default AccountPage;
